'use client';

/*
 * `use client` — Next.js (RSC) uchun.
 *
 * Bu komponentlar React hook'laridan foydalanadi (`useState`, `useId`,
 * `useRef`), demak ular server komponenti bo'la olmaydi. Direktiva
 * bo'lmasa `apps/web` buildi yiqiladi.
 *
 * Vite ilovalari (`miniapp`, `admin`) uchun bu qator hech narsa
 * qilmaydi — u yerda hammasi baribir klientda ishlaydi.
 */
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type Locale } from '@aps/contracts';
import { cx, discountPercent, formatMoneyValue, moneyUnit } from '../lib/format.js';
import { IconCheck, IconLogo } from './icons.js';

/**
 * Primitiv komponentlar.
 *
 * Har biri `components.css` dagi klasslardan foydalanadi — komponent
 * faylida `style` atributi orqali RANG berilmaydi. Dinamik qiymat faqat
 * CSS o'zgaruvchisi orqali uzatiladi (masalan kategoriya rangi `--cat`).
 */

/* ============================== Button ============================== */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'sm';
  block?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      // Standart `type` — "button". `submit` ni forma ataylab beradi;
      // aks holda har tugma formani jo'natib yuboradi.
      type={type === 'submit' ? 'submit' : type === 'reset' ? 'reset' : 'button'}
      className={cx(
        'aps-btn',
        `aps-btn--${variant}`,
        size === 'sm' && 'aps-btn--sm',
        block && 'aps-btn--block',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ============================= PriceTag ============================= */

export interface PriceTagProps {
  /** Tiyinda, string (kontraktdagi `Money`). */
  value: string;
  oldValue?: string | null;
  locale: Locale;
  size?: 'md' | 'sm';
  /** Chegirma foizini ko'rsatish. */
  showDiscount?: boolean;
}

/**
 * Narx.
 *
 * Summa `tabular-nums` bilan chiziladi va ming ajratgich uzluksiz probel —
 * shunda ro'yxatda raqamlar "sakramaydi" (docs/03-UI-UX.md).
 */
export function PriceTag({
  value,
  oldValue = null,
  locale,
  size = 'md',
  showDiscount = true,
}: PriceTagProps): ReactNode {
  const discount = showDiscount ? discountPercent(value, oldValue) : null;

  return (
    <span className={cx('aps-price', size === 'sm' && 'aps-price--sm')}>
      {/*
        Ikki guruh: summa+birlik va eski narx+chegirma. Tor kartada ikkinchi
        guruh BUTUNLAY keyingi qatorga tushadi; alohida `margin-left` bo'lsa
        u qator boshida ham suriladi.
      */}
      <span className="aps-price__main">
        <span className="aps-price__value">{formatMoneyValue(value)}</span>
        <span className="aps-price__unit">{moneyUnit(locale)}</span>
      </span>
      {oldValue !== null && discount !== null ? (
        <span className="aps-price__extra">
          <s className="aps-price__old">{formatMoneyValue(oldValue)}</s>
          <span className="aps-price__discount">−{discount}%</span>
        </span>
      ) : null}
    </span>
  );
}

/* =============================== Chip =============================== */

export interface ChipProps {
  children: ReactNode;
  /** Kategoriya rangiga bo'yaladi (`--cat` merosi). */
  accent?: boolean;
  className?: string;
}

export function Chip({ children, accent = false, className }: ChipProps): ReactNode {
  return (
    <span className={cx('aps-chip', accent && 'aps-chip--accent', className)}>{children}</span>
  );
}

/* ============================ FilterChip ============================ */

export interface FilterChipProps {
  label: string;
  active: boolean;
  onToggle: () => void;
  count?: number;
}

export function FilterChip({ label, active, onToggle, count }: FilterChipProps): ReactNode {
  return (
    <button
      type="button"
      className="aps-filter-chip"
      // Tanlangan holat `aria-pressed` orqali — skrinriderga ham yetadi,
      // CSS ham shunga qarab bo'yaydi. Ikki manbali holat bo'lmaydi.
      aria-pressed={active}
      onClick={onToggle}
    >
      {label}
      {count === undefined ? null : ` (${String(count)})`}
    </button>
  );
}

/* ============================= Skeleton ============================= */

export interface SkeletonProps {
  width?: string;
  height?: string;
  className?: string;
  /** Skrinriderga "yuklanmoqda" deb aytadi. */
  label?: string;
}

export function Skeleton({ width, height = '1em', className, label }: SkeletonProps): ReactNode {
  return (
    <span
      className={cx('aps-skeleton', className)}
      style={{ width: width ?? '100%', height }}
      role="status"
      aria-busy="true"
      aria-label={label ?? 'Yuklanmoqda'}
    />
  );
}

/* ============================ EmptyState ============================ */

export interface EmptyStateProps {
  title: string;
  hint?: string;
  icon?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, hint, icon, action }: EmptyStateProps): ReactNode {
  return (
    <div className="aps-empty">
      {icon}
      <p className="aps-empty__title">{title}</p>
      {hint === undefined ? null : <p className="aps-empty__hint">{hint}</p>}
      {action}
    </div>
  );
}

/* ============================== Stepper ============================== */

export interface StepperProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  /** Skrinrider uchun: qaysi tovarning miqdori. */
  label: string;
}

/**
 * Miqdor o'zgartirgich.
 *
 * Chegaralar KOMPONENT ICHIDA majburlanadi: `onChange` hech qachon
 * `min` dan past yoki `max` dan yuqori qiymat bilan chaqirilmaydi.
 */
export function Stepper({ value, min = 1, max = 999, onChange, label }: StepperProps): ReactNode {
  const clamp = (next: number): number => Math.min(max, Math.max(min, next));

  return (
    <span className="aps-stepper" role="group" aria-label={label}>
      <button
        type="button"
        className="aps-stepper__btn"
        onClick={() => {
          onChange(clamp(value - 1));
        }}
        disabled={value <= min}
        aria-label="Kamaytirish"
      >
        −
      </button>
      <span className="aps-stepper__value" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        className="aps-stepper__btn"
        onClick={() => {
          onChange(clamp(value + 1));
        }}
        disabled={value >= max}
        aria-label="Ko‘paytirish"
      >
        +
      </button>
    </span>
  );
}

/* ============================== Toggle ============================== */

export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}

export function Toggle({ checked, onChange, label, disabled = false }: ToggleProps): ReactNode {
  return (
    <button
      type="button"
      className="aps-toggle"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className="aps-toggle__knob" />
    </button>
  );
}

/* ============================= OptionRow ============================= */

export interface OptionRowProps {
  label: string;
  hint?: string;
  selected?: boolean;
  onSelect?: () => void;
  trailing?: ReactNode;
  leading?: ReactNode;
}

export function OptionRow({
  label,
  hint,
  selected = false,
  onSelect,
  trailing,
  leading,
}: OptionRowProps): ReactNode {
  return (
    <button
      type="button"
      className="aps-option"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
    >
      {leading}
      <span className="aps-option__body">
        {label}
        {hint === undefined ? null : <span className="aps-option__hint">{hint}</span>}
      </span>
      {trailing}
    </button>
  );
}

/* ============================== TabBar ============================== */

export interface TabItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Faol holatdagi ikonka (masalan to'ldirilgan uy). Berilmasa `icon` qoladi. */
  activeIcon?: ReactNode;
  badge?: number;
}

export interface TabBarProps {
  items: readonly TabItem[];
  activeId: string;
  onSelect: (id: string) => void;
}

export function TabBar({ items, activeId, onSelect }: TabBarProps): ReactNode {
  return (
    <nav className="aps-tabbar" aria-label="Asosiy menyu">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className="aps-tabbar__item"
          aria-current={item.id === activeId ? 'page' : undefined}
          onClick={() => {
            onSelect(item.id);
          }}
        >
          {item.id === activeId && item.activeIcon !== undefined ? item.activeIcon : item.icon}
          {item.badge !== undefined && item.badge > 0 ? (
            <span className="aps-tabbar__badge">{item.badge > 99 ? '99+' : item.badge}</span>
          ) : null}
          <span className="aps-tabbar__label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

/* ============================= SearchBar ============================= */

export interface SearchBarProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onValueChange: (next: string) => void;
  onClear?: () => void;
  leading?: ReactNode;
}

export function SearchBar({
  value,
  onValueChange,
  onClear,
  leading,
  placeholder,
  ...rest
}: SearchBarProps): ReactNode {
  return (
    <div className="aps-search">
      {leading}
      <input
        {...rest}
        className="aps-search__input"
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onValueChange(event.target.value);
        }}
      />
      {value.length > 0 ? (
        <button
          type="button"
          className="aps-search__clear"
          aria-label="Tozalash"
          onClick={() => {
            onValueChange('');
            onClear?.();
          }}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

/* ============================ BottomSheet ============================ */

export interface BottomSheetProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Pastdan chiqadigan panel.
 *
 * Escape va fon bosilganda yopiladi; ochilganda fokus ichkariga o'tadi
 * va sahifa scroll'i to'xtaydi — aks holda TWA'da panel ustidan sahifa
 * siljib ketadi.
 */
export function BottomSheet({ open, title, onClose, children }: BottomSheetProps): ReactNode {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="aps-sheet__backdrop" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="aps-sheet"
        role="dialog"
        aria-modal="true"
        {...(title === undefined ? {} : { 'aria-labelledby': titleId })}
        tabIndex={-1}
      >
        <div className="aps-sheet__grip" />
        {title === undefined ? null : (
          <h2 id={titleId} className="aps-sheet__title">
            {title}
          </h2>
        )}
        {children}
      </div>
    </>
  );
}

/* ============================== Toast ============================== */

export type ToastTone = 'info' | 'success' | 'danger';

/** Toast ichidagi bitta amal ("Savatga o'tish"). */
export interface ToastAction {
  label: string;
  onSelect: () => void;
}

export interface ToastOptions {
  /** Sarlavha ostidagi qisqa izoh — masalan, qaysi tovar qo'shilgani. */
  description?: string;
  /** Natijadan keyingi eng mantiqiy qadam. Bor bo'lsa xabar uzoqroq turadi. */
  action?: ToastAction;
  /**
   * Bir xil `key` li xabar YANGISI bilan almashadi. Ketma-ket uch marta "savatga qo'shildi"
   * uch alohida karta emas, bitta yangilangan karta bo'ladi.
   */
  key?: string;
}

interface ToastEntry {
  id: number;
  message: string;
  tone: ToastTone;
  description?: string;
  action?: ToastAction;
  key?: string;
  durationMs: number;
}

export interface ToastApi {
  /** Xabar ko'rsatadi. `tone` standart holatda `info`. */
  show: (message: string, tone?: ToastTone, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Xabar ekranda turadigan vaqt — demodagi `2600` bilan bir xil. */
const TOAST_MS = 2600;

/** Amali bor xabarni o'qish va bosishga ulgurish uchun ko'proq vaqt. */
const TOAST_ACTION_MS = 4500;

/**
 * Bir vaqtda ko'rinadigan xabarlar soni.
 *
 * Demoda BITTA slot bor va yangi xabar eskisini almashtiradi. Bu
 * ATAYLAB takrorlanmadi: admin panelda operator ketma-ket ikki amal
 * bajaradi va birinchisining XATOSI ikkinchisining "saqlandi" xabari
 * bilan jimgina o'chib ketardi. Uchta — ekranni to'ldirmaydigan,
 * lekin xatoni yutmaydigan chegara.
 */
const TOAST_MAX = 3;

const TOAST_ICON: Record<ToastTone, string> = { info: 'i', success: '', danger: '!' };

/**
 * Bildirishnoma provayderi.
 *
 * Ilova ildizida bir marta qo'yiladi. Har amaldan keyin javob berish
 * uchun: savatga qo'shildi, saqlandi, status o'zgardi, xato.
 */
export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = 'info', options: ToastOptions = {}) => {
      nextId.current += 1;
      const id = nextId.current;
      const durationMs = options.action === undefined ? TOAST_MS : TOAST_ACTION_MS;
      const entry: ToastEntry = {
        id,
        message,
        tone,
        durationMs,
        ...(options.description === undefined ? {} : { description: options.description }),
        ...(options.action === undefined ? {} : { action: options.action }),
        ...(options.key === undefined ? {} : { key: options.key }),
      };
      setItems((current) =>
        [
          ...current.filter((item) => options.key === undefined || item.key !== options.key),
          entry,
        ].slice(-TOAST_MAX),
      );
      setTimeout(() => {
        dismiss(id);
      }, durationMs);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext value={api}>
      {children}
      {/*
        Jonli soha HAR DOIM DOMda turadi, xabar bilan birga
        yaratilmaydi. Ekran o'quvchi `aria-live` sohasini oldindan
        kuzatishi kerak: soha xabar bilan birga paydo bo'lsa, u
        o'zgarishni umuman e'lon qilmaydi.
      */}
      <div className="aps-toasts" role="status" aria-live="polite">
        {items.map((entry) => (
          <div
            className="aps-toast"
            data-tone={entry.tone}
            key={entry.id}
            style={{ '--toast-ms': `${String(entry.durationMs)}ms` } as CSSProperties}
          >
            <span className="aps-toast__icon" aria-hidden="true">
              {entry.tone === 'success' ? <IconCheck size={18} /> : TOAST_ICON[entry.tone]}
            </span>
            <span className="aps-toast__body">
              <span className="aps-toast__title">{entry.message}</span>
              {entry.description === undefined ? null : (
                <span className="aps-toast__desc">{entry.description}</span>
              )}
            </span>
            {entry.action === undefined ? null : (
              <button
                type="button"
                className="aps-toast__action"
                onClick={() => {
                  entry.action?.onSelect();
                  dismiss(entry.id);
                }}
              >
                {entry.action.label}
              </button>
            )}
            <span className="aps-toast__bar" aria-hidden="true" />
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

/**
 * Bildirishnoma yuborish.
 *
 * Provayder yo'q bo'lsa xabar JIMGINA yo'qolmaydi — bu xato va u
 * darhol ko'rinishi kerak, aks holda "nega hech narsa chiqmadi?"
 * savoliga javob topilmasdi.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error('useToast — <ToastProvider> ichida ishlatiladi');
  }
  return api;
}

/* ============================= BrandLogo ============================= */

export interface BrandLogoProps {
  /** Belgi o'lchami, px. Maket: ~34. */
  markSize?: number;
  /**
   * Yakuniy logotip uchun O'RIN (slot).
   *
   * Haqiqiy logotip (tishli g'ildirak + avtomobil siluet, `docs/design/MAKET-TOPSHIRIQ.md`
   * 5-bo'lim) hali yo'q. U kelganda shu prop orqali (yoki `IconLogo` o'rniga to'g'ridan-to'g'ri)
   * `<img>`/SVG beriladi — wordmark va joylashuv o'zgarmaydi. Berilmasa vaqtinchalik
   * `IconLogo` belgisi chiziladi.
   */
  mark?: ReactNode;
}

/**
 * Brend yozuvi: belgi + "AvtoPartShop". "Avto"+"Part" matn rangida, "Shop" aksent rangida.
 * Yozuv rasm emas, MATN — til va mavzuga moslashadi, ekran o'quvchi o'qiydi.
 */
export function BrandLogo({ markSize = 34, mark }: BrandLogoProps): ReactNode {
  return (
    <span className="aps-brand">
      <span className="aps-brand__mark">{mark ?? <IconLogo size={markSize} />}</span>
      <span className="aps-brand__word">
        AvtoPart
        <span className="aps-brand__accent">Shop</span>
      </span>
    </span>
  );
}
